import { useTranslation } from 'react-i18next';
import InputField from '../../../../components/ui/InputField';
import SelectField from '../../../../components/ui/SelectField';
import SegmentedTabs from '../../../../components/SegmentedTabs';
import AssetDetailSection from '../../../assets/components/AssetDetailSection';
import type {
    ControlUnitStatus,
    LockStatus,
    StationCreationForm,
} from '../stationCreationForm';
import type { StationAccessType } from '../../../../api/station/stationApiTypes';
import { MAX_POSITIONS } from '../stationCreationValidation';

interface StationStepProps {
    form: StationCreationForm;
    update: <K extends keyof StationCreationForm>(key: K, value: StationCreationForm[K]) => void;
}

const ACCESS_OPTIONS: StationAccessType[] = ['none', 'raspberry_pi', 'dormakaba'];

/**
 * Locks and the control unit are asked as a CHOICE, not as free text.
 *
 * "Which locks are installed?" assumed the tenant already has them and knows
 * what they are — most do not, and a blank field gave us nothing to act on. The
 * two answers we actually need are different: *is it already there* (then tell
 * us what it is), or *does kolula supply it* (then we make a proposal). Both are
 * an inventory declaration; ops still does the provisioning (spec §4).
 */
const LOCK_STATUS_OPTIONS: LockStatus[] = ['installed', 'needed'];
const CONTROL_UNIT_OPTIONS: ControlUnitStatus[] = ['existing', 'kolula', 'undecided'];

/**
 * Step 2 — the Station (= depot). First the station type (room vs compartment),
 * then its positions, then the access declaration (inventory-style, PRD-001 —
 * ops connects the lock; the wizard only records the intent) and an installed-
 * hardware note. See `docs/plans/object-and-station-creation-flow.md` §2/§4.
 */
const StationStep = ({ form, update }: StationStepProps) => {
    const { t } = useTranslation();

    const accessLabels: Record<StationAccessType, string> = {
        none: t('station_create.station.access_none'),
        raspberry_pi: t('station_create.station.access_raspberry_pi'),
        dormakaba: t('station_create.station.access_dormakaba'),
    };

    const lockStatusLabels: Record<LockStatus, string> = {
        installed: t('station_create.station.lock_status_installed'),
        needed: t('station_create.station.lock_status_needed'),
    };

    const controlUnitLabels: Record<ControlUnitStatus, string> = {
        existing: t('station_create.station.control_unit_existing'),
        kolula: t('station_create.station.control_unit_kolula'),
        undecided: t('station_create.station.control_unit_undecided'),
    };

    return (
        <AssetDetailSection
            title={t('station_create.station.title')}
            subtitle={t('station_create.station.subtitle')}
        >
            <div className={'flex flex-col gap-5'}>
                <SegmentedTabs<'compartment' | 'room'>
                    items={[
                        { key: 'compartment', label: t('station_create.station.type_compartment') },
                        { key: 'room', label: t('station_create.station.type_room') },
                    ]}
                    value={form.stationType}
                    onChange={(key) => update('stationType', key)}
                />

                {/* A new location was named on step 1 and holds exactly this
                    station, so it is not named twice (PO, 2026-09-07). An
                    existing location may already hold other stations — Segmenta
                    runs an office with a meeting room and a kolula locker in it
                    — so there the station keeps its own name. */}
                {form.locationMode === 'existing' ? (
                    <InputField
                        label={t('station_create.station.name_label')}
                        placeholder={t('station_create.station.name_placeholder')}
                        value={form.stationName}
                        onChange={(v) => update('stationName', v)}
                    />
                ) : (
                    <p className={'text-sm text-foreground/60'}>
                        {t('station_create.station.name_from_location', {
                            name: form.newLocationName.trim(),
                        })}
                    </p>
                )}

                {form.stationType === 'compartment' && (
                    <div className={'grid grid-cols-1 gap-5 sm:grid-cols-2'}>
                        <InputField
                            labelAbove
                            type={'number'}
                            integerOnly
                            min={1}
                            max={MAX_POSITIONS}
                            label={t('station_create.station.position_count_label')}
                            help={t('station_create.station.position_count_help')}
                            placeholder={'10'}
                            value={form.positionCount}
                            onChange={(v) => update('positionCount', v)}
                        />
                        <InputField
                            labelAbove
                            type={'number'}
                            integerOnly
                            min={0}
                            label={t('station_create.station.buffer_label')}
                            help={t('station_create.station.buffer_help')}
                            placeholder={'0'}
                            value={form.bufferCount}
                            onChange={(v) => update('bufferCount', v)}
                        />
                    </div>
                )}

                <SelectField
                    labelAbove
                    label={t('station_create.station.access_label')}
                    help={t('station_create.station.access_help')}
                    options={ACCESS_OPTIONS}
                    optionLabels={accessLabels}
                    value={form.accessType}
                    onChange={(v) => update('accessType', v as StationAccessType)}
                />

                {/* Asked in the order the tenant can answer it: which locks, then
                    which control unit, and only THEN what happens next. The
                    "we'll be in touch" line used to sit between the two, so it
                    promised a callback before the tenant had finished saying
                    what they have. */}
                {form.accessType !== 'none' && (
                    <div className={'flex flex-col gap-3'}>
                        <SelectField
                            labelAbove
                            label={t('station_create.station.lock_status_label')}
                            options={LOCK_STATUS_OPTIONS}
                            optionLabels={lockStatusLabels}
                            placeholder={t('station_create.station.choose_placeholder')}
                            value={form.lockStatus}
                            onChange={(v) => update('lockStatus', v as LockStatus)}
                        />
                        {form.lockStatus === 'installed' && (
                            <InputField
                                labelAbove
                                label={t('station_create.station.lock_declaration_label')}
                                placeholder={t('station_create.station.lock_declaration_placeholder')}
                                value={form.lockDeclaration}
                                onChange={(v) => update('lockDeclaration', v)}
                            />
                        )}

                        {form.accessType === 'raspberry_pi' && (
                            <>
                                <SelectField
                                    labelAbove
                                    label={t('station_create.station.control_unit_label')}
                                    help={t('station_create.station.control_unit_help')}
                                    options={CONTROL_UNIT_OPTIONS}
                                    optionLabels={controlUnitLabels}
                                    placeholder={t('station_create.station.choose_placeholder')}
                                    value={form.controlUnitStatus}
                                    onChange={(v) => update('controlUnitStatus', v as ControlUnitStatus)}
                                />
                                {form.controlUnitStatus === 'existing' && (
                                    <InputField
                                        labelAbove
                                        label={t('station_create.station.hardware_label')}
                                        placeholder={t('station_create.station.hardware_placeholder')}
                                        value={form.installedHardware}
                                        onChange={(v) => update('installedHardware', v)}
                                    />
                                )}
                            </>
                        )}

                        <p className={'rounded-lg bg-foreground/5 px-4 py-3 text-sm text-foreground/60'}>
                            {t('station_create.station.connect_hint')}
                        </p>
                    </div>
                )}

                <InputField
                    labelAbove
                    label={t('station_create.station.description_label')}
                    placeholder={t('station_create.station.description_placeholder')}
                    value={form.description}
                    onChange={(v) => update('description', v)}
                />
            </div>
        </AssetDetailSection>
    );
};

export default StationStep;
